<template>
  <div class="device-group-checklist">
    <button
      v-for="row in rows"
      :key="row.key"
      type="button"
      role="checkbox"
      class="device-group-checklist__row"
      :aria-checked="ARIA_CHECKED[row.state]"
      :disabled="row.disabled"
      @click="$emit('toggle', row.key)"
    >
      <input
        type="checkbox"
        class="form-check-input mt-0 pe-none"
        tabindex="-1"
        aria-hidden="true"
        :checked="row.state === 'checked'"
        :indeterminate="row.state === 'partial'"
      />
      <span class="fw-medium text-truncate">{{ row.label }}</span>
      <span class="ms-auto text-body-secondary text-nowrap">
        {{ row.countLabel }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import type { GroupSelectionState } from "@/utils/groupSelection";

export interface DeviceGroupChecklistRow {
  key: number;
  label: string;
  countLabel: string;
  state: GroupSelectionState;
  disabled?: boolean;
}

/**
 * A list of tri-state group checkboxes. Each row is a `role="checkbox"` button, so a partly
 * selected group reads as "mixed"; the visible checkbox inside it is decoration. Clicking a row only
 * emits `toggle`: the caller works out the new selection.
 */
defineProps<{
  rows: DeviceGroupChecklistRow[];
}>();

defineEmits<{
  toggle: [key: number];
}>();

const ARIA_CHECKED: Record<GroupSelectionState, "true" | "false" | "mixed"> = {
  checked: "true",
  partial: "mixed",
  unchecked: "false",
};
</script>

<style scoped lang="scss">
// Past five groups the list scrolls.
.device-group-checklist {
  border-bottom: 1px solid var(--phoenix-border-color);
  max-height: 172px;
  overflow-y: auto;
}

.device-group-checklist__row {
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 6px 12px;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--phoenix-gray-50);
  }

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
    outline-offset: -2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
}
</style>
